import { useState } from "react";
import {
    FaPlay,
    FaCheckCircle,
    FaTimesCircle,
    FaChartLine,
    FaCoins,
} from "react-icons/fa";

import AdminPage from "./components/AdminPage";
import AdminCard from "./components/AdminCard";
import AdminTable from "./components/AdminTable";
import PrimaryButton from "./components/PrimaryButton";

import { runBacktest } from "./api/backtestApi";


export default function Backtest() {

    const [from, setFrom] =
        useState("");

    const [to, setTo] =
        useState("");

    const [maxMatches, setMaxMatches] =
        useState(100);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    const [result, setResult] =
        useState(null);


    const executeBacktest = async () => {

        if (!from || !to) {

            setError(
                "Please select both a start and end date."
            );

            return;
        }


        setLoading(true);
        setError("");
        setResult(null);


        try {

            const data =
                await runBacktest({
                    from,
                    to,
                    maxMatches:
                        Number(maxMatches)
                });


            setResult(data);

        } catch (err) {

            console.error(
                "Backtest error:",
                err
            );

            setError(
                err?.response?.data?.message ||
                "Failed to run backtest."
            );

        } finally {

            setLoading(false);

        }

    };


    const predictions =
        result?.summary?.predictions;


    const valueBets =
        result?.summary?.valueBets;


    const predictionAccuracy =
        predictions?.accuracy ??
        (
            predictions?.total > 0
                ? (
                    predictions.correct /
                    predictions.total *
                    100
                )
                : 0
        );


    const formatDate = (date) => {

        if (!date)
            return "-";


        return new Date(date).toLocaleString(
            "en-GB",
            {
                dateStyle: "short",
                timeStyle: "short"
            }
        );

    };


    const getPredictionResult =
        (match) => {

            const prediction =
                match?.prediction;


            if (!prediction?.available)
                return null;


            const results =
                prediction.result || {};


            for (
                const [
                    market,
                    value
                ]
                of Object.entries(results)
            ) {

                if (value?.predicted) {

                    return {
                        market,
                        probability:
                            value.probability,
                        correct:
                            value.actual === market
                    };

                }

            }


            return null;

        };


    return (

        <AdminPage
            title="Backtest"
            subtitle="Test the prediction algorithm against historical match results."
        >


            {/* ------------------------------------------------ */}
            {/* CONTROLS */}
            {/* ------------------------------------------------ */}

            <AdminCard className="p-6 mb-6">

                <div
                    className="
                        grid
                        grid-cols-1
                        md:grid-cols-4
                        gap-4
                        items-end
                    "
                >

                    <div>

                        <label
                            className="
                                block
                                text-sm
                                text-gray-400
                                mb-2
                            "
                        >
                            From
                        </label>

                        <input
                            type="date"
                            value={from}
                            onChange={(e) =>
                                setFrom(e.target.value)
                            }
                            className="
                                w-full
                                bg-black/30
                                border
                                border-green-500/20
                                rounded-lg
                                px-4
                                py-3
                                text-white
                                focus:outline-none
                                focus:border-green-400
                            "
                        />

                    </div>


                    <div>

                        <label
                            className="
                                block
                                text-sm
                                text-gray-400
                                mb-2
                            "
                        >
                            To
                        </label>

                        <input
                            type="date"
                            value={to}
                            onChange={(e) =>
                                setTo(e.target.value)
                            }
                            className="
                                w-full
                                bg-black/30
                                border
                                border-green-500/20
                                rounded-lg
                                px-4
                                py-3
                                text-white
                                focus:outline-none
                                focus:border-green-400
                            "
                        />

                    </div>


                    <div>

                        <label
                            className="
                                block
                                text-sm
                                text-gray-400
                                mb-2
                            "
                        >
                            Max Matches
                        </label>

                        <input
                            type="number"
                            min="1"
                            max="10000"
                            value={maxMatches}
                            onChange={(e) =>
                                setMaxMatches(
                                    e.target.value
                                )
                            }
                            className="
                                w-full
                                bg-black/30
                                border
                                border-green-500/20
                                rounded-lg
                                px-4
                                py-3
                                text-white
                                focus:outline-none
                                focus:border-green-400
                            "
                        />

                    </div>


                    <PrimaryButton
                        onClick={executeBacktest}
                        disabled={loading}
                    >

                        <div
                            className="
                                flex
                                items-center
                                justify-center
                                gap-2
                            "
                        >

                            <FaPlay
                                size={13}
                            />

                            {loading
                                ? "Running..."
                                : "Run Backtest"
                            }

                        </div>

                    </PrimaryButton>

                </div>


                {error && (

                    <div
                        className="
                            mt-4
                            rounded-lg
                            bg-red-900/30
                            border
                            border-red-500/20
                            text-red-400
                            px-4
                            py-3
                        "
                    >

                        {error}

                    </div>

                )}

            </AdminCard>


            {/* ------------------------------------------------ */}
            {/* RESULTS */}
            {/* ------------------------------------------------ */}

            {result && (

                <>

                    {/* ---------------------------------------- */}
                    {/* SUMMARY */}
                    {/* ---------------------------------------- */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            sm:grid-cols-2
                            lg:grid-cols-4
                            gap-4
                            mb-6
                        "
                    >

                        <AdminCard className="p-5">

                            <div
                                className="
                                    flex
                                    items-center
                                    justify-between
                                "
                            >

                                <div>

                                    <p
                                        className="
                                            text-gray-400
                                            text-sm
                                        "
                                    >
                                        Matches
                                    </p>

                                    <p
                                        className="
                                            text-3xl
                                            font-bold
                                            text-white
                                            mt-1
                                        "
                                    >
                                        {result.matches?.total ?? 0}
                                    </p>

                                </div>

                                <FaChartLine
                                    className="
                                        text-green-400
                                        text-xl
                                    "
                                />

                            </div>

                        </AdminCard>


                        <AdminCard className="p-5">

                            <p
                                className="
                                    text-gray-400
                                    text-sm
                                "
                            >
                                Prediction Coverage
                            </p>

                            <p
                                className="
                                    text-3xl
                                    font-bold
                                    text-white
                                    mt-1
                                "
                            >
                                {predictions?.coverage ?? 0}%
                            </p>

                            <p
                                className="
                                    text-xs
                                    text-gray-500
                                    mt-1
                                "
                            >
                                {predictions?.available ?? 0}
                                {" "}
                                predictions available
                            </p>

                        </AdminCard>


                        <AdminCard className="p-5">

                            <p
                                className="
                                    text-gray-400
                                    text-sm
                                "
                            >
                                Prediction Accuracy
                            </p>

                            <p
                                className="
                                    text-3xl
                                    font-bold
                                    text-green-400
                                    mt-1
                                "
                            >
                                {Number(
                                    predictionAccuracy
                                ).toFixed(2)}
                                %
                            </p>

                            <p
                                className="
                                    text-xs
                                    text-gray-500
                                    mt-1
                                "
                            >
                                {predictions?.correct ?? 0}
                                {" "}
                                correct /
                                {" "}
                                {predictions?.total ?? 0}
                            </p>

                        </AdminCard>


                        <AdminCard className="p-5">

                            <div
                                className="
                                    flex
                                    items-center
                                    justify-between
                                "
                            >

                                <div>

                                    <p
                                        className="
                                            text-gray-400
                                            text-sm
                                        "
                                    >
                                        Value Bets
                                    </p>

                                    <p
                                        className="
                                            text-3xl
                                            font-bold
                                            text-white
                                            mt-1
                                        "
                                    >
                                        {valueBets?.total ?? 0}
                                    </p>

                                    <p
                                        className="
                                            text-xs
                                            text-gray-500
                                            mt-1
                                        "
                                    >
                                        ROI:
                                        {" "}
                                        {valueBets?.roi ?? 0}%
                                    </p>

                                </div>

                                <FaCoins
                                    className="
                                        text-yellow-400
                                        text-xl
                                    "
                                />

                            </div>

                        </AdminCard>

                    </div>


                    {/* ---------------------------------------- */}
                    {/* PERIOD / CONFIGURATION */}
                    {/* ---------------------------------------- */}

                    <AdminCard className="p-5 mb-6">

                        <div
                            className="
                                flex
                                flex-col
                                md:flex-row
                                md:items-center
                                md:justify-between
                                gap-4
                            "
                        >

                            <div>

                                <p
                                    className="
                                        text-sm
                                        text-gray-400
                                    "
                                >
                                    Backtest Period
                                </p>

                                <p
                                    className="
                                        text-white
                                        font-semibold
                                        mt-1
                                    "
                                >
                                    {result.period?.from}
                                    {" "}
                                    →{" "}
                                    {result.period?.to}
                                </p>

                            </div>


                            <div
                                className="
                                    flex
                                    flex-wrap
                                    gap-3
                                    text-sm
                                "
                            >

                                <span
                                    className="
                                        px-3
                                        py-1
                                        rounded-full
                                        bg-black/30
                                        text-gray-300
                                    "
                                >
                                    Algorithm:
                                    {" "}
                                    {result.configuration
                                        ?.algorithmPresetId ??
                                        "Active"}
                                </span>


                                <span
                                    className="
                                        px-3
                                        py-1
                                        rounded-full
                                        bg-black/30
                                        text-gray-300
                                    "
                                >
                                    Prediction:
                                    {" "}
                                    {result.configuration
                                        ?.predictionPresetId ??
                                        "Active"}
                                </span>

                            </div>

                        </div>

                    </AdminCard>


                    {/* ---------------------------------------- */}
                    {/* MARKET PERFORMANCE */}
                    {/* ---------------------------------------- */}

                    <AdminCard className="p-6 mb-6">

                        <h2
                            className="
                                text-lg
                                font-semibold
                                text-white
                                mb-4
                            "
                        >
                            Market Performance
                        </h2>


                        <div
                            className="
                                grid
                                grid-cols-1
                                md:grid-cols-3
                                gap-4
                            "
                        >

                            {Object.entries(
                                result.summary?.markets || {}
                            ).map(
                                ([
                                    market,
                                    data
                                ]) => (

                                    <div
                                        key={market}
                                        className="
                                            bg-black/20
                                            border
                                            border-green-500/10
                                            rounded-lg
                                            p-4
                                        "
                                    >

                                        <div
                                            className="
                                                flex
                                                justify-between
                                                items-center
                                            "
                                        >

                                            <span
                                                className="
                                                    text-gray-300
                                                    font-medium
                                                "
                                            >
                                                {market
                                                    .replaceAll(
                                                        "_",
                                                        " "
                                                    )
                                                    .toUpperCase()}
                                            </span>

                                            <span
                                                className="
                                                    text-green-400
                                                    font-bold
                                                "
                                            >
                                                {data.accuracy}%
                                            </span>

                                        </div>


                                        <div
                                            className="
                                                mt-3
                                                h-2
                                                rounded-full
                                                bg-gray-800
                                                overflow-hidden
                                            "
                                        >

                                            <div
                                                className="
                                                    h-full
                                                    bg-green-500
                                                "
                                                style={{
                                                    width:
                                                        `${Math.min(
                                                            data.accuracy,
                                                            100
                                                        )}%`
                                                }}
                                            />

                                        </div>


                                        <p
                                            className="
                                                text-xs
                                                text-gray-500
                                                mt-2
                                            "
                                        >
                                            {data.correct}
                                            {" "}
                                            correct out of
                                            {" "}
                                            {data.total}
                                        </p>

                                    </div>

                                )
                            )}

                        </div>

                    </AdminCard>


                    {/* ---------------------------------------- */}
                    {/* MATCH RESULTS */}
                    {/* ---------------------------------------- */}

                    <AdminTable
                        headers={[
                            "Match",
                            "Date",
                            "Score",
                            "Actual",
                            "Prediction",
                            "Probability",
                            "Status",
                        ]}
                        loading={false}
                        empty={
                            !result.results?.length
                        }
                        emptyMessage="No backtest results."
                    >

                        {result.results?.map(
                            (item) => {

                                const prediction =
                                    getPredictionResult(
                                        item
                                    );


                                return (

                                    <tr
                                        key={
                                            item.match?.id
                                        }
                                        className="
                                            border-t
                                            border-green-500/10
                                            hover:bg-white/5
                                            transition
                                        "
                                    >

                                        {/* MATCH */}

                                        <td className="p-4">

                                            <div
                                                className="
                                                    text-white
                                                    font-medium
                                                "
                                            >
                                                {item.match?.home}
                                            </div>

                                            <div
                                                className="
                                                    text-gray-500
                                                    text-sm
                                                "
                                            >
                                                {item.match?.away}
                                            </div>

                                        </td>


                                        {/* DATE */}

                                        <td
                                            className="
                                                p-4
                                                text-gray-400
                                                whitespace-nowrap
                                            "
                                        >
                                            {formatDate(
                                                item.match?.date
                                            )}
                                        </td>


                                        {/* SCORE */}

                                        <td
                                            className="
                                                p-4
                                                text-white
                                                font-bold
                                            "
                                        >
                                            {item.actual?.homeGoals}
                                            {" - "}
                                            {item.actual?.awayGoals}
                                        </td>


                                        {/* ACTUAL */}

                                        <td className="p-4">

                                            <span
                                                className="
                                                    px-3
                                                    py-1
                                                    rounded-full
                                                    text-xs
                                                    bg-blue-900/30
                                                    text-blue-400
                                                "
                                            >
                                                {item.actual?.result
                                                    ?.replace(
                                                        "_",
                                                        " "
                                                    )}
                                            </span>

                                        </td>


                                        {/* PREDICTION */}

                                        <td className="p-4">

                                            {prediction ? (

                                                <span
                                                    className={`
                                                        px-3
                                                        py-1
                                                        rounded-full
                                                        text-xs
                                                        ${
                                                            prediction.correct
                                                                ? "bg-green-900/40 text-green-400"
                                                                : "bg-red-900/40 text-red-400"
                                                        }
                                                    `}
                                                >

                                                    {prediction.market
                                                        .replace(
                                                            "_",
                                                            " "
                                                        )}

                                                </span>

                                            ) : (

                                                <span
                                                    className="
                                                        text-gray-600
                                                    "
                                                >
                                                    No prediction
                                                </span>

                                            )}

                                        </td>


                                        {/* PROBABILITY */}

                                        <td
                                            className="
                                                p-4
                                                text-gray-300
                                            "
                                        >

                                            {prediction
                                                ? `${prediction.probability}%`
                                                : "-"}

                                        </td>


                                        {/* STATUS */}

                                        <td className="p-4">

                                            {!item.prediction
                                                ?.available ? (

                                                <span
                                                    className="
                                                        text-gray-500
                                                        text-sm
                                                    "
                                                >
                                                    Unavailable
                                                </span>

                                            ) : prediction?.correct ? (

                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-2
                                                        text-green-400
                                                    "
                                                >

                                                    <FaCheckCircle />

                                                    Correct

                                                </div>

                                            ) : (

                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-2
                                                        text-red-400
                                                    "
                                                >

                                                    <FaTimesCircle />

                                                    Incorrect

                                                </div>

                                            )}

                                        </td>

                                    </tr>

                                );

                            }
                        )}

                    </AdminTable>

                </>

            )}

        </AdminPage>

    );

}